import { getMixpanelClient } from '@Src/mfe.config';
import { TrackingPayloadBuilder, type RequestContext } from '@Analytics/trackingPayloadBuilder';
import { EVENT, PURPOSE_TYPE } from '@Constants/tracking.constants';
import { PaymentMethodChoice } from '@Constants/paymentMethod.constants';

export const trackPaymentMethodSelected = (
    method: PaymentMethodChoice,
    isEligibleForDigital: boolean,
) => {
    const context: RequestContext = method === PaymentMethodChoice.Wire ? 'Send' : 'Payment';
    const trackingObject = new TrackingPayloadBuilder(context)
        .withPurposeType(PURPOSE_TYPE)
        .withDigitalEligible(isEligibleForDigital)
        .withCustomField('Payment Method', method)
        .build();

    getMixpanelClient()?.track(EVENT.PaymentMethodSelected, trackingObject);
};

export const trackRequestStarted = (digitalEligible: boolean) => {
    // V1: certifid-v2-webapp-frontend/src/trackings/modules/clientCollectRequests.mixpanel.js → trackRequestStarted (wire side)
    // V1 also emits MPP Enabled, MPP Prepaid, Recipient Type, Referral Source, and Digital Verification Failed.
    // Those signals don't exist in request-mfe today; tracked as a follow-up plumbing PR.
    const trackingObject = new TrackingPayloadBuilder('Send')
        .withDigitalEligible(digitalEligible)
        .build();

    getMixpanelClient()?.track(EVENT.RequestStarted, trackingObject);
};

export const trackTransferAmountSelected = (transferAmountSelected: string) => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/TransferAmount.vue
    const trackingObject = new TrackingPayloadBuilder('Send')
        .withTransferAmountSelected(transferAmountSelected)
        .build();

    getMixpanelClient()?.track(EVENT.TransferAmountSelected, trackingObject);
};

export const trackMppSkipped = () => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/MPP.vue (no Transfer Amount Selected on this one)
    const trackingObject = new TrackingPayloadBuilder('Send').build();

    getMixpanelClient()?.track(EVENT.MppSkipped, trackingObject);
};

export const trackMppPurchased = (transferAmountSelected: string) => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/PaymentInfo.vue (paid=true branch)
    const trackingObject = new TrackingPayloadBuilder('Send')
        .withTransferAmountSelected(transferAmountSelected)
        .build();

    getMixpanelClient()?.track(EVENT.MppPurchased, trackingObject);
};

export const trackMppCancelled = (transferAmountSelected: string) => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/PaymentInfo.vue (paid=false branch)
    const trackingObject = new TrackingPayloadBuilder('Send')
        .withTransferAmountSelected(transferAmountSelected)
        .build();

    getMixpanelClient()?.track(EVENT.MppCancelled, trackingObject);
};

export const trackBankInformationViewed = (
    mppPurchased: boolean,
    transferAmountSelected: string,
) => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/Acknowledgement.vue
    // V1 emits: base props + MPP Purchased + Transfer Amount Selected. No Validation Type / Status / Bank Account Type.
    const trackingObject = new TrackingPayloadBuilder('Send')
        .withMppPurchased(mppPurchased)
        .withTransferAmountSelected(transferAmountSelected)
        .build();

    getMixpanelClient()?.track(EVENT.BankInformationViewed, trackingObject);
};

export const trackPdfDownloaded = () => {
    // V1: certifid-v2-webapp-frontend/src/views/client/send/DownloadPDF.vue
    // V1 emits base props only. No MPP Purchased on this event.
    const trackingObject = new TrackingPayloadBuilder('Send').build();

    getMixpanelClient()?.track(EVENT.PdfDownloaded, trackingObject);
};
